import React, { useEffect } from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Link } from "gatsby";
import { useSpring, animated } from "@react-spring/web";

const Header = ({
  title,
  subTitle,
  author,
  publishedAt,
  categories,
  tags,
  thumbnailImage,
  overview,
  featured,
}) => {
  const image = getImage(thumbnailImage?.image?.asset);

  // Parallax spring animations with immediate response
  const [{ scrollY }, setScrollY] = useSpring(() => ({
    scrollY: 0,
    immediate: true,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      clamp: true,
    },
  }));

  // Parallax movement configuration
  const backgroundSpring = useSpring({
    transform: scrollY.to((y) => `translate3d(0, ${y * -0.03}px, 0)`),
    immediate: true,
  });

  const overlaySpring = useSpring({
    transform: scrollY.to((y) => `translate3d(0, ${y * 0.03}px, 0)`),
    immediate: true,
    opacity: scrollY.to((y) => Math.min(0.7 + y * 0.0005, 0.85)),
  });

  const contentSpring = useSpring({
    transform: scrollY.to((y) => `translate3d(0, ${y * 0.01}px, 0)`),
    immediate: true,
  });

  // Handle scroll with RAF for smooth performance
  useEffect(() => {
    let frame = 0;

    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setScrollY({ scrollY: window.scrollY });
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(frame);
    };
  }, [setScrollY]);

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  return (
    <article className="bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Image with Parallax */}
        {image && (
          <>
            <animated.div
              className="absolute inset-0 h-full"
              style={backgroundSpring}
            >
              <GatsbyImage
                image={image}
                alt={thumbnailImage?.altText || title}
                className="w-full h-[110%] object-cover"
              />
            </animated.div>
            {/* Overlay with Parallax */}
            <animated.div
              className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/20"
              style={overlaySpring}
            />
          </>
        )}

        {/* Content with Parallax */}
        <animated.div
          className={`relative ${
            image ? "text-white py-24 lg:py-32" : "text-black py-16 lg:py-24"
          }`}
          style={contentSpring}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* Featured badge — inside hero so fixed navbar overlays image, not banner */}
              {featured && (
                <div className="mb-6">
                  <span
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium uppercase tracking-wider bg-logo1 text-black"
                  >
                    ⭐ Featured Article
                  </span>
                </div>
              )}

              {/* Categories */}
              {categories && categories.length > 0 && (
                <div className="mb-6">
                  <div className="flex flex-wrap gap-3">
                    {categories.map((category) => (
                      <Link
                        key={category.slug?.current}
                        to={`/blog/category/${category.slug?.current}`}
                        className={`px-4 py-2 rounded-full text-sm font-medium uppercase tracking-wider transition-all duration-200 ${
                          image
                            ? "bg-white/90 hover:bg-white text-black"
                            : "bg-logo1 hover:bg-logo1/90 text-black"
                        }`}
                      >
                        {category.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Title */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {title}
              </h1>

              {/* Subtitle */}
              {subTitle && (
                <p
                  className={`text-xl md:text-2xl mb-8 leading-relaxed max-w-3xl ${
                    image ? "text-gray-200" : "text-gray-600"
                  }`}
                >
                  {subTitle}
                </p>
              )}

              {/* Meta Information */}
              <div className="flex flex-wrap items-center gap-6 text-sm">
                {/* Author */}
                {author && (
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        image ? "bg-white/20 text-white" : "bg-logo1 text-black"
                      }`}
                    >
                      {author.name?.charAt(0)}
                    </div>
                    <div>
                      <p
                        className={`font-semibold ${
                          image ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {author.name}
                      </p>
                    </div>
                  </div>
                )}

                {/* Publication Date */}
                {publishedAt && (
                  <div className="flex items-center gap-2">
                    <svg
                      className={`w-4 h-4 ${
                        image ? "text-gray-300" : "text-gray-500"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <time
                      dateTime={publishedAt}
                      className={`${image ? "text-gray-300" : "text-gray-600"}`}
                    >
                      {formatDate(publishedAt)}
                    </time>
                  </div>
                )}

                {/* Tags */}
                {tags && tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {tags.slice(0, 3).map((tag) => (
                      <Link
                        key={tag.slug?.current}
                        to={`/blog/tag/${tag.slug?.current}`}
                        className={`px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider transition-all duration-200 ${
                          image
                            ? "bg-white/10 hover:bg-white/20 text-gray-300 border border-white/20"
                            : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
                        }`}
                      >
                        #{tag.name}
                      </Link>
                    ))}
                    {tags.length > 3 && (
                      <span
                        className={`px-3 py-1 rounded-full text-xs ${
                          image ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        +{tags.length - 3} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </animated.div>
      </div>

      {/* Reading Progress Bar */}
      <div className="h-1 bg-gray-200 sticky top-0 z-50">
        <div
          id="reading-progress"
          className="h-full bg-logo1 transition-all duration-100 ease-out"
          style={{ width: "0%" }}
        ></div>
      </div>
    </article>
  );
};

export default Header;
